// HTML sanitizer for Orcanos field values. Some fields can contain HTML
// (per the source design doc). We allow a small set of formatting tags
// and strip everything else.

import DOMPurify from 'dompurify';
import { getSettings } from '../settings/settingsStore';

const CONFIG = {
  ALLOWED_TAGS: [
    'b', 'i', 'u', 'em', 'strong', 'span', 'br',
    'a', 'p', 'ul', 'ol', 'li', 'img',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height'],
  ALLOW_DATA_ATTR: false,
  // Force any link to open in a new tab and never expose window.opener.
  ADD_ATTR: ['target', 'rel'],
};

// URLs inside field HTML come from the Orcanos rich-text editor and are written
// relative to the Orcanos web root, not to us — this app is served from its own
// host, so an unresolved `covarisFTP/foo.png` 404s against OUR origin
// and the browser paints a broken-image box. Everything here exists to turn
// whatever Orcanos stored into an absolute URL on the Orcanos host.
//
// Shapes seen / anticipated:
//   https://host///covarisFTP/x.png   absolute, extra slashes after the host
//   ///covarisFTP/x.png               root-relative, extra leading slashes
//   /covarisFTP/x.png                 root-relative  → <origin>/covarisFTP/x.png
//   covarisFTP/x.png                  doc-relative   → <baseUrl>covarisFTP/x.png
//   //cdn.host/x.png                  protocol-relative
export function resolveOrcanosUrl(raw) {
  let src = String(raw ?? '').trim();
  if (!src) return src;

  // Leave non-network schemes (data:, blob:, mailto:, tel:, #anchor) alone.
  if (/^(data:|blob:|mailto:|tel:|#)/i.test(src)) return src;

  // Absolute: only collapse the duplicated slashes right after the host.
  if (/^https?:\/\//i.test(src)) {
    return src.replace(/^(https?:\/\/)([^/]+)\/+/i, (_, proto, host) => `${proto}${host}/`);
  }

  // Protocol-relative (exactly two slashes then a host) — just pick a scheme.
  if (/^\/\/[^/]/.test(src)) return 'https:' + src;

  // Three or more leading slashes is a mangled root-relative path.
  src = src.replace(/^\/{2,}/, '/');

  try {
    // `new URL` gets both cases right: a leading "/" resolves against the
    // origin, anything else against the baseUrl directory.
    return new URL(src, getSettings().baseUrl).href;
  } catch {
    return src;
  }
}

// Orcanos serves field images from `https://<host>/covarisFTP/…`, which is
// gated on an Orcanos session cookie. We authenticate the API with a Basic
// header, not a session, so a bare request 302s to /covarisFtp/Error ("Access
// Denied") and the browser paints a broken-image box. The tenant accepts a
// token query that serves the bytes without a session — `imageUrlQuery` in
// this account's BOM settings. Appending it is what makes the Image column render at all.
//
// Scoped deliberately: the token goes ONLY on the Orcanos host AND only under
// the tenant's file folder, so it can never be appended to some third-party
// image URL that happens to appear in a field and leak there.
//
// That folder is `<tenant>FTP` — `/covarisFTP/` for Covaris, where this was
// written. It is derived from the session's Orcanos URL (whose last segment is
// the tenant), never hard-coded, now that the module serves every account.
export function tenantFileFolder(baseUrl) {
  try {
    const parts = new URL(baseUrl).pathname.split('/').filter(Boolean);
    return parts.length ? `${parts[parts.length - 1]}ftp`.toLowerCase() : '';
  } catch {
    return '';
  }
}

export function withImageToken(url) {
  const { baseUrl, imageUrlQuery } = getSettings();
  if (!imageUrlQuery) return url;
  try {
    const u = new URL(url);
    if (u.host !== new URL(baseUrl).host) return url;
    const folder = tenantFileFolder(baseUrl);
    if (!folder || !u.pathname.toLowerCase().includes(`/${folder}/`)) return url;
    if (u.searchParams.has(imageUrlQuery.split('=')[0])) return url;
    // Concatenated rather than rebuilt from `u` so the stored path is passed
    // through byte-for-byte.
    return url + (url.includes('?') ? '&' : '?') + imageUrlQuery;
  } catch {
    return url;
  }
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    const href = node.getAttribute('href');
    // withImageToken only fires under /covarisFTP/, so an ordinary Orcanos web
    // link is untouched while a link to an attachment gets the same access it
    // needs as an inline image.
    if (href) node.setAttribute('href', withImageToken(resolveOrcanosUrl(href)));
  }
  if (node.tagName === 'IMG') {
    const src = node.getAttribute('src');
    if (src) {
      const fixed = withImageToken(resolveOrcanosUrl(src));
      node.setAttribute('src', fixed);
      if (!node.getAttribute('alt')) {
        node.setAttribute('alt', fixed.split('/').pop().split('?')[0] || 'image');
      }
    }
  }
});

// Safety net for an image the browser still couldn't fetch — most likely the
// `imageUrlQuery` token no longer being accepted, which would otherwise turn
// the whole Image column into broken-image boxes with no explanation. Swap it
// for a link: clicking it is a top-level navigation, which carries the Orcanos
// session cookie, so a user logged into Orcanos still sees the image.
//
// `error` doesn't bubble; listen in the capture phase at the window.
if (typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    (e) => {
      const el = e.target;
      if (!el || el.tagName !== 'IMG' || el.dataset.imgFallback) return;
      // ⚠️ This listener is on the WINDOW, and the platform hosts other modules.
      // Unscoped, one visit to the BOM viewer would turn every broken image in
      // Traceability or Training into a link. Only BOM-rendered images qualify.
      if (!el.closest || !el.closest('.bom-scope')) return;
      el.dataset.imgFallback = '1';

      const url = el.currentSrc || el.src;
      console.warn('[img] FAILED to load: ' + url);

      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = 'field-image-link';
      a.textContent = el.getAttribute('alt') || 'image';
      a.title = 'Open in Orcanos — ' + url;
      el.replaceWith(a);
    },
    true
  );
}

export function sanitizeHtml(html) {
  if (html == null) return '';
  return DOMPurify.sanitize(String(html), CONFIG);
}
